import { useGetTransitionalCareRequests } from "apollo/hooks/queries";
import { isLoading } from "apollo/utils";
import {
  CARE_NECESSITIES,
  DOCS_FORM_NAMES,
  MAX_REQUESTS,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REQUEST_FORM_FIELDS,
} from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import {
  TransitionalCareFormData,
  TransitionalCareImportedAuctionRequest,
} from "core/types";
import { getUnixTime } from "date-fns";
import {
  Banner,
  Button,
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
  ControlledTimeInput,
} from "ds/ui";
import { useOntologyOptions } from "ds_legacy/components/SelectInput/Ontology";
import { TEXT_AREA_MAX_CHARACTERS } from "dsl/ecosystems/PatientForms/components";
import { getDefaultAddingState } from "dsl/ecosystems/PatientForms/TransitionalCare/Form/FormSections/SectionE/RequestListContext/utils";
import {
  isEmptyManualRequest,
  mergeRequestListWithImported,
} from "dsl/ecosystems/RecareDocs/Modal/Forms/TransitionalCare/requestListFormUtils";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { FormFieldConfigProps } from "..";
import { SourceWrapper } from "../../utils";

export const LegalRepresentativeConsentField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  useEffect(() => {
    if (!consentProvided) {
      setValue("legal_representative_last_name", "");
      setValue("legal_representative_first_name", "");
      setValue("legal_representative_location_street", "");
      setValue("legal_representative_location_street_number", "");
      setValue("legal_representative_location_zipcode", "");
      setValue("legal_representative_location_city", "");
    }
  }, [consentProvided, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .confirmedWithLegalRep
      }
      name="legal_representative_consent_provided"
    />
  );
};

export const LegalRepresentativeFirstNameField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepFirstName
      }
      name="legal_representative_first_name"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLastNameField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepName
      }
      name="legal_representative_last_name"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationStreetField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepStreet
      }
      name="legal_representative_location_street"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationStreetNumberField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepStreetNumber
      }
      name="legal_representative_location_street_number"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationZipcodeField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepZipCode
      }
      name="legal_representative_location_zipcode"
      placeholder=" "
    />
  );
};

export const LegalRepresentativeLocationCityField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const consentProvided = useWatch({
    name: "legal_representative_consent_provided",
  });

  return (
    <ControlledInput
      isDisabled={!consentProvided}
      label={
        translations.patientForms.transitionalCareForm.contactInformation
          .legalRepCity
      }
      name="legal_representative_location_city"
      placeholder=" "
    />
  );
};

export const PriorTreatmentEndDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const startDate = useWatch({ name: "prior_treatment_start_date" });

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.hospitalStay.releaseDate
      }
      maxValue={getUnixTime(new Date())}
      minValue={startDate ?? undefined}
      name="prior_treatment_end_date"
    />
  );
};

export const TransitionalCareEndDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const startDate = useWatch({ name: "transitional_care_start_date" });

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.hospitalStay
          .endDateTransitionalCare
      }
      minValue={startDate ?? undefined}
      name="transitional_care_end_date"
    />
  );
};

export const PatientConfirmationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const patientConfirmation = useWatch({
    name: "transitional_care_patient_confirmation",
  });

  useEffect(() => {
    if (!patientConfirmation) {
      setValue("transitional_care_patient_confirmation_date", undefined);
    } else {
      setValue("transitional_care_patient_confirmation_explanation", "");
    }
  }, [patientConfirmation, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .toggleConfirmedWithPatient
      }
      name="transitional_care_patient_confirmation"
    />
  );
};

export const PatientConfirmationDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const patientConfirmation = useWatch({
    name: "transitional_care_patient_confirmation",
  });

  if (!patientConfirmation) return null;

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.sectionB.dateFieldLabel
      }
      maxValue={getUnixTime(new Date())}
      name="transitional_care_patient_confirmation_date"
    />
  );
};

export const PatientConfirmationExplanationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const patientConfirmation = useWatch({
    name: "transitional_care_patient_confirmation",
  });

  if (patientConfirmation) return null;

  return (
    <ControlledTextarea
      isRequired
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .textFieldExplanationLabel
      }
      maxLength={TEXT_AREA_MAX_CHARACTERS}
      name="transitional_care_patient_confirmation_explanation"
      placeholder=" "
    />
  );
};

export const SuitableProvidersMoreThanTwentyField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const moreThanTwenty = useWatch({
    name: "transitional_care_suitable_providers_more_than_twenty",
  });

  useEffect(() => {
    if (moreThanTwenty) {
      setValue("transitional_care_suitable_providers_count", undefined);
    }
  }, [moreThanTwenty, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .toggleNumberOfProviders
      }
      name="transitional_care_suitable_providers_more_than_twenty"
    />
  );
};

export const SuitableProvidersCountField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const moreThanTwenty = useWatch({
    name: "transitional_care_suitable_providers_more_than_twenty",
  });

  if (moreThanTwenty) return null;

  const items = Array.from({ length: 21 }, (_, i) => ({
    id: i.toString(),
    label: i.toString(),
    value: i,
  }));

  return (
    <ControlledSelect
      items={items}
      label={
        translations.patientForms.transitionalCareForm.sectionB
          .dropdownNumberOfProviders
      }
      name="transitional_care_suitable_providers_count"
      renderItem={(item) => item.label}
      variant="outline"
    />
  );
};

export const AftercareApplicationSubmittedField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const applicationSubmitted = useWatch({
    name: "aftercare_application_submitted",
  });

  useEffect(() => {
    if (!applicationSubmitted) {
      setValue("aftercare_application_date", undefined);
    } else {
      setValue("aftercare_application_explanation", "");
    }
  }, [applicationSubmitted, setValue]);

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionC
          .applicationSubmitted
      }
      name="aftercare_application_submitted"
    />
  );
};

export const AftercareApplicationDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const applicationSubmitted = useWatch({
    name: "aftercare_application_submitted",
  });

  if (!applicationSubmitted) return null;

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.sectionC.applicationDate
      }
      maxValue={getUnixTime(new Date())}
      name="aftercare_application_date"
    />
  );
};

export const AftercareApplicationExplanationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const applicationSubmitted = useWatch({
    name: "aftercare_application_submitted",
  });

  if (applicationSubmitted) return null;

  return (
    <ControlledTextarea
      isRequired
      label={
        translations.patientForms.transitionalCareForm.sectionC
          .textFieldDescription
      }
      maxLength={TEXT_AREA_MAX_CHARACTERS}
      name="aftercare_application_explanation"
      placeholder=" "
    />
  );
};

export const AftercareCareLevelAvailableField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });

  useEffect(() => {
    if (careLevelAvailable) {
      setValue("aftercare_care_level_applied", undefined);
      setValue("aftercare_care_level_explanation", "");
      setValue("aftercare_care_level_applied_date", undefined);
    }
  }, [careLevelAvailable, setValue]);

  return (
    <ControlledCheckbox
      label={translations.patientForms.transitionalCareForm.sectionC.careLevel}
      name="aftercare_care_level_available"
    />
  );
};

export const AftercareCareLevelAppliedField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });
  const careLevelApplied = useWatch({ name: "aftercare_care_level_applied" });

  useEffect(() => {
    if (!careLevelApplied) {
      setValue("aftercare_care_level_applied_date", undefined);
    } else {
      setValue("aftercare_care_level_explanation", "");
    }
  }, [careLevelApplied, setValue]);

  if (careLevelAvailable) return null;

  return (
    <ControlledCheckbox
      label={
        translations.patientForms.transitionalCareForm.sectionC.careLevelApplied
      }
      name="aftercare_care_level_applied"
    />
  );
};

export const AftercareCareLevelAppliedDateField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });
  const careLevelApplied = useWatch({ name: "aftercare_care_level_applied" });

  if (careLevelAvailable || !careLevelApplied) return null;

  return (
    <ControlledDatePicker
      label={
        translations.patientForms.transitionalCareForm.sectionC
          .careLevelAppliedDate
      }
      maxValue={getUnixTime(new Date())}
      name="aftercare_care_level_applied_date"
    />
  );
};

export const AftercareCareLevelExplanationField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const careLevelAvailable = useWatch({
    name: "aftercare_care_level_available",
  });
  const careLevelApplied = useWatch({ name: "aftercare_care_level_applied" });

  if (careLevelAvailable || careLevelApplied) return null;

  return (
    <ControlledTextarea
      label={
        translations.patientForms.transitionalCareForm.sectionC.textFieldLabel
      }
      maxLength={TEXT_AREA_MAX_CHARACTERS}
      name="aftercare_care_level_explanation"
      placeholder=" "
    />
  );
};

export const CareNecessityField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { setValue } = useFormContext<TransitionalCareFormData>();
  const careNecessity = useWatch({ name: "care_necessity" });

  useEffect(() => {
    if (careNecessity !== CARE_NECESSITIES.FULL_STOP_OUTSIDE_ENTITLEMENT) {
      setValue("aftercare_start_date", undefined);
    }
  }, [careNecessity, setValue]);

  const options = [
    {
      label: translations.patientForms.transitionalCareForm.sectionF.bulletOne,
      value: CARE_NECESSITIES.AFTER_CARE_ENSURED_DURING,
    },
    {
      label: translations.patientForms.transitionalCareForm.sectionF.bulletTwo,
      value: CARE_NECESSITIES.AFTER_CARE_COULD_BE_ENSURED,
    },
    {
      label:
        translations.patientForms.transitionalCareForm.sectionF.bulletThree,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>
          fieldName="aftercare_start_date"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledDatePicker
            label={
              translations.patientForms.transitionalCareForm.sectionF
                .dateFieldLabel
            }
            name="aftercare_start_date"
          />
        </SourceWrapper>
      ),
      value: CARE_NECESSITIES.FULL_STOP_OUTSIDE_ENTITLEMENT,
    },
    {
      label: translations.patientForms.transitionalCareForm.sectionF.bulletFour,
      value: CARE_NECESSITIES.NO_FURTHER_NEED,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={translations.patientForms.transitionalCareForm.sectionF.title}
      name="care_necessity"
      options={options}
      size="md"
    />
  );
};

function RequestListSubForm({
  getRequests,
  isRequestListLoading = false,
}: {
  isRequestListLoading?: boolean;
  getRequests: () => Promise<
    TransitionalCareImportedAuctionRequest[] | null | undefined
  >;
}) {
  const { control, getValues, setValue } =
    useFormContext<TransitionalCareFormData>();
  const { append, fields, remove } = useFieldArray({
    control,
    name: "request_list",
  });
  const internetPortalUsed = useWatch({ name: "internet_portal_used" });
  const translations = useTranslations();
  const getRequestsRef = useRef(getRequests);
  getRequestsRef.current = getRequests;

  const [importedRequestsEmpty, setImportedRequestsEmpty] = useState<
    boolean | null
  >(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    if (internetPortalUsed !== PREDICAMENT_STATE_YES) {
      setImportedRequestsEmpty(null);
      return;
    }
    let cancelled = false;
    getRequestsRef.current().then((requests) => {
      if (!cancelled) {
        setImportedRequestsEmpty((requests?.length ?? 0) === 0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [internetPortalUsed]);

  const selectOptions = useOntologyOptions({
    type: ONTOLOGIES.TRANSITIONAL_CARE_REQUEST_STATUS,
  });

  const showTriggerOnly =
    internetPortalUsed === PREDICAMENT_STATE_NO && fields.length === 0;

  const handleImportRequests = async () => {
    setBannerDismissed(false);
    const importedRequestList = await getRequests();
    setValue(
      "request_list",
      mergeRequestListWithImported(
        getValues("request_list") ?? [],
        importedRequestList ?? [],
      ),
    );
  };

  const showSectionHeader =
    fields.length > 0 ||
    (internetPortalUsed === PREDICAMENT_STATE_YES &&
      importedRequestsEmpty === true);

  if (showTriggerOnly) {
    return (
      <Button
        className="w-full"
        color="primary"
        id="add-request-trigger"
        isDisabled={isRequestListLoading}
        startContent={<PlusIcon className="h-4 w-4" />}
        variant="ghost"
        onPress={() => append(getDefaultAddingState(1))}
      >
        {
          translations.patientForms.transitionalCareForm.sectionE
            .buttonAddRequestManually
        }
      </Button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {showSectionHeader && (
        <div className="flex flex-row justify-between gap-2">
          <h4 className="text-md font-bold">
            {
              translations.patientForms.transitionalCareForm.sectionE
                .requestListTitle
            }
          </h4>
          {internetPortalUsed === PREDICAMENT_STATE_YES && (
            <Button
              color="primary"
              id="import-requests"
              isLoading={isRequestListLoading}
              size="sm"
              variant="ghost"
              onPress={handleImportRequests}
            >
              {
                translations.patientForms.transitionalCareForm.sectionE
                  .buttonRefresh
              }
            </Button>
          )}
        </div>
      )}

      {internetPortalUsed === PREDICAMENT_STATE_YES &&
        importedRequestsEmpty === true &&
        !bannerDismissed && (
          <Banner
            color="warning"
            message={
              translations.patientForms.transitionalCareForm.sectionE
                .bannerNoImportedRequests
            }
            withClose
            onClose={() => setBannerDismissed(true)}
          />
        )}

      {fields.map((request, index) => (
        <div key={request.id} className="flex w-full flex-col gap-4">
          <div className="flex w-full">
            <h5 className="text-sm font-bold">
              {`${index + 1}. ${
                request.manual
                  ? translations.patientForms.transitionalCareForm.sectionE
                      .requestManuallyAddedTitle
                  : translations.patientForms.transitionalCareForm.sectionE
                      .requestImportedFromRecareTitle
              }`}
            </h5>
          </div>
          <div className="flex w-full flex-col gap-2">
            <div className="flex flex-col gap-2">
              <ControlledInput
                isDisabled={isRequestListLoading}
                isRequired
                label={
                  translations.patientForms.transitionalCareForm.sectionE
                    .editName
                }
                name={`request_list.${index}.${REQUEST_FORM_FIELDS.CAREPROVIDER_NAME}`}
                placeholder=" "
              />
              <ControlledInput
                isDisabled={isRequestListLoading}
                isRequired
                label={
                  translations.patientForms.transitionalCareForm.sectionE
                    .editStreet
                }
                name={`request_list.${index}.${REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ADDRESS}`}
                placeholder=" "
              />
              <div className="flex gap-2">
                <ControlledInput
                  className="flex-1"
                  isDisabled={isRequestListLoading}
                  isRequired
                  label={
                    translations.patientForms.transitionalCareForm.sectionE
                      .editZipCode
                  }
                  name={`request_list.${index}.${REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_ZIPCODE}`}
                  placeholder=" "
                />
                <ControlledInput
                  className="flex-2"
                  isDisabled={isRequestListLoading}
                  isRequired
                  label={
                    translations.patientForms.transitionalCareForm.sectionE
                      .editCity
                  }
                  name={`request_list.${index}.${REQUEST_FORM_FIELDS.CAREPROVIDER_ADDRESS_CITY}`}
                  placeholder=" "
                />
              </div>
              <div className="flex gap-2">
                <ControlledDatePicker
                  isDisabled={isRequestListLoading}
                  isRequired
                  label={
                    translations.patientForms.transitionalCareForm.sectionE
                      .editDate
                  }
                  maxValue={getUnixTime(new Date())}
                  name={`request_list.${index}.${REQUEST_FORM_FIELDS.REQUEST_SENT_DATE}`}
                />
                <ControlledTimeInput
                  isDisabled={isRequestListLoading}
                  isRequired
                  label={
                    translations.patientForms.transitionalCareForm.sectionE
                      .editTime
                  }
                  name={`request_list.${index}.${REQUEST_FORM_FIELDS.REQUEST_SENT_TIME}`}
                />
              </div>
              <ControlledSelect
                isDisabled={isRequestListLoading}
                isRequired
                items={selectOptions}
                label={
                  translations.patientForms.transitionalCareForm.sectionE
                    .editStatus
                }
                name={`request_list.${index}.${REQUEST_FORM_FIELDS.REQUEST_STATUS}`}
                renderItem={(item) => item.label}
                variant="outline"
              />
            </div>
            <div>
              <Button
                color="danger"
                id={`remove-request-${index}`}
                isDisabled={isRequestListLoading}
                startContent={<TrashIcon className="h-4 w-4" />}
                variant="light"
                onPress={() => remove(index)}
              >
                {
                  translations.patientForms.transitionalCareForm.sectionE
                    .buttonRemoveRequest
                }
              </Button>
            </div>
            <hr className="mt-4 mb-2 border-gray-400" />
          </div>
        </div>
      ))}
      <div className="flex">
        <Button
          className="w-full"
          color="primary"
          id="add-request"
          isDisabled={fields.length >= MAX_REQUESTS || isRequestListLoading}
          startContent={<PlusIcon className="h-4 w-4" />}
          variant="ghost"
          onPress={() =>
            append({
              careprovider_address_address: "",
              careprovider_address_city: "",
              careprovider_address_zipcode: "",
              careprovider_name: "",
              edited: true,
              id: 0,
              manual: true,
              request_id: null,
              request_sent_date: null,
              request_sent_time: null,
              request_status: null,
            })
          }
        >
          {
            translations.patientForms.transitionalCareForm.sectionE
              .buttonAddRequestManually
          }
        </Button>
      </div>
    </div>
  );
}

export const ProviderRequestListRadioGroup = ({
  auction,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.TRANSITIONAL_CARE>) => {
  const { clearErrors, getValues, setValue } =
    useFormContext<TransitionalCareFormData>();
  const internetPortalUsed = useWatch({ name: "internet_portal_used" });
  const isFirstRender = useRef(true);

  const [queryProgress, getRequests] = useGetTransitionalCareRequests({
    auctionId: auction?.id ?? 0,
  });

  const getRequestsRef = useRef(getRequests);
  getRequestsRef.current = getRequests;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (
      internetPortalUsed !== PREDICAMENT_STATE_YES &&
      internetPortalUsed !== PREDICAMENT_STATE_NO
    ) {
      return;
    }

    const currentList = (getValues("request_list") ?? []).filter(
      (r) => !isEmptyManualRequest(r),
    );

    if (internetPortalUsed === PREDICAMENT_STATE_YES) {
      getRequestsRef.current().then((requests) => {
        setValue(
          "request_list",
          mergeRequestListWithImported(currentList, requests ?? []),
        );
        clearErrors("request_list");
      });
    } else {
      setValue("request_list", currentList);
      clearErrors("request_list");
    }
  }, [internetPortalUsed, clearErrors, getValues, setValue]);

  const options = [
    {
      label: translations.actions.yes,
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
  ];

  return (
    <div className="flex w-full flex-col">
      <ControlledRadioGroup
        isDisabled={isLoading(queryProgress)}
        label={
          translations.patientForms.transitionalCareForm.sectionE
            .internetbasedPortals
        }
        name="internet_portal_used"
        options={options}
        size="md"
      />
      <hr className="my-6 border-gray-400" />
      <RequestListSubForm
        getRequests={getRequests}
        isRequestListLoading={isLoading(queryProgress)}
      />
    </div>
  );
};
